import React from 'react';
import { Box, Tooltip, TooltipProps } from '@mui/material';
import useForkRef from '@mui/utils/useForkRef';
import useActionableAnnotation from './useActionableAnnotation';

export function hasAnnotationActions(node: React.ReactNode): boolean {
  return React.Children.toArray(node).some(child => {
    if (!React.isValidElement(child)) return false;
    const type = typeof child.type === 'string' ? child.type : (child.type as any).muiName || (child.type as any).render?.name;
    return ['button', 'a', 'Button', 'IconButton', 'Checkbox', 'Radio', 'ListItemButton'].includes(type)
      || typeof child.props.onClick === 'function' || Boolean(child.props.href) || hasAnnotationActions(child.props.children);
  });
}

/** Same MUI tooltip and content, with the Admin action-annotation interaction. */
export default function ActionableTooltip({ children, title, preserveTriggerClick = false, ...props }: TooltipProps & {
  preserveTriggerClick?: boolean;
}) {
  const actionable = hasAnnotationActions(title);
  const annotation = useActionableAnnotation({ enabled: actionable,
    onDismiss: event => props.onClose?.(event ?? new Event('annotationclose')) });
  const child = children as React.ReactElement<any>;
  const anchorRef = useForkRef((child as any).ref, annotation.anchor);
  // Keep MUI controlled from the first render, including asynchronously loaded content.
  if (!actionable) return <Tooltip {...props} title={title} open={props.open ?? annotation.open}
    onOpen={event => { annotation.preview(); props.onOpen?.(event); }}
    onClose={event => { annotation.hidePreview(); props.onClose?.(event); }}>{children}</Tooltip>;

  const sidePlacement = props.placement?.startsWith('right') || props.placement?.startsWith('left');
  return <Tooltip {...props}
    PopperProps={{ ...props.PopperProps, modifiers: [
      ...(sidePlacement ? [{ name: 'flip', options: { fallbackPlacements: ['left-start', 'top', 'bottom'] } }] : []),
      ...(props.PopperProps?.modifiers || [])
    ] }}
    title={<Box ref={annotation.content} sx={{ display: 'contents' }} onClickCapture={event => {
      const action = (event.target as Element).closest('button, a, input[type="checkbox"], input[type="radio"], [role="menuitem"], [role="option"], [data-annotation-action]');
      if (action && !action.hasAttribute('disabled') && action.getAttribute('aria-disabled') !== 'true') annotation.close(event);
    }} onClick={event => event.stopPropagation()}>{title}</Box>}
    open={annotation.open || Boolean(props.open)}
    onOpen={event => { annotation.preview(); props.onOpen?.(event); }}
    onClose={event => { annotation.hidePreview(); if (!annotation.pinned) props.onClose?.(event); }}>
    {React.cloneElement(child, { ref: anchorRef, 'aria-expanded': annotation.open || Boolean(props.open),
      onClick: (event: React.MouseEvent) => {
        annotation.pin();
        if (preserveTriggerClick) child.props.onClick?.(event);
        else event.stopPropagation();
      } })}
  </Tooltip>;
}
